// ./src/components/features/Budget/BudgetItemsList/BudgetItemsList.tsx
//
// Event budget items list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit / Payments / Delete only), rights-gated per row,
// selection-ready. Each row shows the derived expected amount plus a paid /
// remaining progress bar. Category name + icon/color come from the shared
// CategoriesContext. Mirrors TasksList (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
  Progress,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { BudgetItem } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonCheckbox, CardSkeletonLines, countCardActions, type CardSkeletonShape } from "@/components/generic";
import { useEntityCommentsDrawer } from "@/components/features/Comments";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG, formatCurrency } from "@/utils";

import { getCalcTypeMeta, getPartyKindIcon, getPaymentMethodLabel, formatDueDate } from "../budgetHelpers";

export interface BudgetItemsListProps {
  data?: BudgetItem[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditItem?: (item: BudgetItem) => void;
  onDeleteItem?: (item: BudgetItem) => void;
  onManagePayments?: (item: BudgetItem) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddItem?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search / filter is narrowing the list. An empty result then means "no
   * match" — NOT "nothing here yet", so show that instead of the Add prompt.
   */
  hasQueryNarrowing?: boolean;
}

interface CategoryMeta {
  name: string | null;
  icon: string | null;
  color: string | null;
}

const itemCurrency = (item: BudgetItem): string => item.currencyCode || "USD";

const CategoryCell: React.FC<{ meta: CategoryMeta | null }> = ({ meta }) => {
  if (!meta?.name) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      {meta.icon ? <AppIcon iconName={meta.icon} size={16} color={meta.color || undefined} /> : null}
      <Text size="md" c={meta.color || undefined} lineClamp={1}>
        {meta.name}
      </Text>
    </Group>
  );
};

const ResponsibleCell: React.FC<{ item: BudgetItem }> = ({ item }) => {
  if (!item.responsibleLabel) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <AppIcon iconName={getPartyKindIcon(item.responsibleKind)} size={15} />
      <Text size="md" lineClamp={1}>
        {item.responsibleLabel}
      </Text>
    </Group>
  );
};

const AmountCell: React.FC<{ item: BudgetItem; compact?: boolean }> = ({ item, compact }) => {
  const currency = itemCurrency(item);
  const expected = Number(item.expectedAmount) || 0;
  const paid = Number(item.paidAmount) || 0;
  const remaining = Number(item.remainingAmount) || 0;
  const pct = expected > 0 ? Math.min(100, Math.round((paid / expected) * 100)) : paid > 0 ? 100 : 0;
  const fullyPaid = expected > 0 && remaining <= 0;

  const lastLine =
    item.lastPaymentAmount != null
      ? [
          `Last: ${formatCurrency(item.lastPaymentAmount, currency)}`,
          item.lastPaymentAt ? formatDueDate(item.lastPaymentAt) : null,
          item.lastPaymentMethod ? getPaymentMethodLabel(item.lastPaymentMethod) : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;

  return (
    <Box style={{ minWidth: compact ? undefined : 240 }}>
      <Text size="md" fw={700}>
        {formatCurrency(expected, currency)}
      </Text>
      <Progress value={pct} size="sm" color={fullyPaid ? "teal" : "blue"} mt={4} mb={4} />
      <Text size="xs" c="dimmed">
        {formatCurrency(paid, currency)} paid
        {fullyPaid ? (
          <Text span c="teal" fw={600}>
            {" · Paid in full"}
          </Text>
        ) : (
          <Text span c="red">
            {` · ${formatCurrency(remaining, currency)} left`}
          </Text>
        )}
      </Text>
      {lastLine ? (
        <Text size="xs" c="dimmed">
          {lastLine}
        </Text>
      ) : null}
    </Box>
  );
};

const ItemIcon: React.FC<{ item: BudgetItem; meta: CategoryMeta | null; size?: number }> = ({ item, meta, size = 34 }) => (
  <ThemeIcon variant="light" color={meta?.color || "grape"} radius="xl" size={size}>
    <AppIcon iconName={meta?.icon || getCalcTypeMeta(item.calcType)?.icon || "Coin"} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  item: BudgetItem;
  onEdit?: (i: BudgetItem) => void;
  onPayments?: (i: BudgetItem) => void;
  onDelete?: (i: BudgetItem) => void;
  onOpenComments?: (i: BudgetItem) => void;
  isDeleting: boolean;
}> = ({ item, onEdit, onPayments, onDelete, onOpenComments, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const canEdit = recordHasRight(item, "edit");
  const showEdit = !!onEdit && canEdit;
  const showPayments = !!onPayments && canEdit;
  const showDelete = !!onDelete && recordHasRight(item, "remove");
  const showComments = !!onOpenComments; // comments are open to any viewer of the item

  if (!showEdit && !showPayments && !showDelete && !showComments) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showComments && onOpenComments && (
        <Tooltip label={t("common:comments.action", { defaultValue: "Comments" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => onOpenComments(item)}
            disabled={isDeleting}
            aria-label={t("common:comments.action", { defaultValue: "Comments" })}
          >
            <AppIcon iconName="Message" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showPayments && onPayments && (
        <Tooltip label="Payments" withArrow position="bottom">
          <ActionIcon variant="subtle" color="teal" size="md" onClick={() => onPayments(item)} disabled={isDeleting} aria-label="Payments">
            <AppIcon iconName="Cash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label="Edit" withArrow position="bottom">
          <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onEdit(item)} disabled={isDeleting} aria-label="Edit">
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label="Delete" withArrow position="bottom">
          <ActionIcon variant="subtle" color="red" size="md" onClick={() => onDelete(item)} loading={isDeleting} disabled={isDeleting} aria-label="Delete">
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  item: BudgetItem;
  meta: CategoryMeta | null;
  onEdit?: (i: BudgetItem) => void;
  onPayments?: (i: BudgetItem) => void;
  onDelete?: (i: BudgetItem) => void;
  onOpenComments?: (i: BudgetItem) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

const BudgetTableRow: React.FC<RowProps> = ({
  item,
  meta,
  onEdit,
  onPayments,
  onDelete,
  onOpenComments,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => (
  <Table.Tr
    style={{
      verticalAlign: "top",
      opacity: isDeleting ? 0.5 : 1,
      backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        {selectable && (
          <Checkbox
            mt={7}
            checked={selected}
            onChange={() => item.id && onToggleSelect?.(item.id)}
            disabled={isDeleting}
            aria-label={item.name || "Select"}
          />
        )}
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <ItemIcon item={item} meta={meta} />
        <Box style={{ minWidth: 0 }}>
          <Text size="md" fw={600} lineClamp={1}>
            {item.name || "—"}
          </Text>
          <Text size="xs" c="dimmed">
            {getCalcTypeMeta(item.calcType)?.label}
          </Text>
        </Box>
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <CategoryCell meta={meta} />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Text size="md" c={item.dueDate ? undefined : "dimmed"} lineClamp={1}>
        {formatDueDate(item.dueDate) || "—"}
      </Text>
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <ResponsibleCell item={item} />
    </Table.Td>
    <Table.Td style={{ width: 240 }}>
      <AmountCell item={item} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <RowActions item={item} onEdit={onEdit} onPayments={onPayments} onDelete={onDelete} onOpenComments={onOpenComments} isDeleting={isDeleting} />
    </Table.Td>
  </Table.Tr>
);

const BudgetTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={14} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Skeleton height={14} width={90} />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={14} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 240 }}>
      <Skeleton height={30} width={160} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const BudgetCardComponent: React.FC<CardProps> = ({
  item,
  meta,
  onEdit,
  onPayments,
  onDelete,
  onOpenComments,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => item.id && onToggleSelect?.(item.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    // Comments (secondary) auto-routes to the LEFT swipe side; edit/remove stay right.
    if (onOpenComments) {
      actions.push({
        key: "comments",
        icon: "Message",
        label: t("common:comments.action", { defaultValue: "Comments" }),
        color: "gray",
        onClick: () => onOpenComments(item),
        disabled: isDeleting,
        secondary: true,
      });
    }

    if (onPayments && recordHasRight(item, "edit")) {
      actions.push({ key: "payments", icon: "Cash", label: "Payments", color: "teal", onClick: () => onPayments(item), disabled: isDeleting, secondary: true });
    }

    if (onEdit && recordHasRight(item, "edit")) {
      actions.push({ key: "edit", icon: "Edit", label: "Edit", color: "blue", onClick: () => onEdit(item), disabled: isDeleting });
    }

    if (onDelete && recordHasRight(item, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: "Delete", color: "red", onClick: () => onDelete(item), loading: isDeleting, disabled: isDeleting });
    }

    return actions;
  }, [onPayments, onEdit, onDelete, onOpenComments, item, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox mt={2} checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={item.name || "Select"} />
        )}
        <ItemIcon item={item} meta={meta} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="md" fw={600} lineClamp={1}>
            {item.name || "—"}
          </Text>
          <Text size="xs" c="dimmed">
            {getCalcTypeMeta(item.calcType)?.label}
          </Text>
          <Box mt={4}>
            <CategoryCell meta={meta} />
          </Box>
          {item.dueDate ? (
            <Group gap={6} wrap="nowrap" mt={2} c="dimmed">
              <AppIcon iconName="CalendarDue" size={13} />
              <Text size="md">{formatDueDate(item.dueDate)}</Text>
            </Group>
          ) : null}
          {item.responsibleLabel ? (
            <Box mt={2}>
              <ResponsibleCell item={item} />
            </Box>
          ) : null}
          <Box mt="xs">
            <AmountCell item={item} compact />
          </Box>
        </Box>
        {showActions && <RowActions item={item} onEdit={onEdit} onPayments={onPayments} onDelete={onDelete} onOpenComments={onOpenComments} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const BudgetCard = React.memo(BudgetCardComponent);

// A line with a category and a payment recorded (most have both — ROADMAP #127) and no due date or responsible person
// (most lack them): name, calc type (xs), category, then the amount block — amounts, an xs row that stands in for the
// progress bar and its margins, what is paid, and the "Last: …" payment line. Measured with a category: 183px against
// the card's 183.8 (a line with no category draws "—" a 4.5px shorter row).
const BudgetCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={7} sizes={['md', 'xs', 'md', 'md', 'xs', 'xs', 'xs']} gap={1} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const BudgetItemsListComponent: React.FC<BudgetItemsListProps> = ({
  data = [],
  loading = false,
  isLoaded,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onEditItem,
  onDeleteItem,
  onManagePayments,
  isRemoving = () => false,
  highlightedId,
  onAddItem,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { isDesktop } = useAppEnvironment();
  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onManagePayments, onEditItem, onDeleteItem, true /* comments — open to every viewer */);
  const categoriesContext = useOptionalCategoriesContext();

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  // Comments row action — opens the reusable drawer for any viewer, without the edit drawer. Mounted once
  // (below) via `comments.drawer`; each row calls `comments.open(...)` from its Comments action.
  const comments = useEntityCommentsDrawer();

  const categoryMeta = useCallback(
    (item: BudgetItem): CategoryMeta | null => {
      if (!item.categoryId) {
        return null;
      }

      const category = categoriesContext?.getById(item.categoryId);

      if (!category) {
        return null;
      }

      return { name: category.name || category.code || null, icon: category.icon ?? null, color: category.color ?? null };
    },
    [categoriesContext]
  );

  const handleEdit = useCallback(
    (item: BudgetItem) => {
      closeAllCards();
      onEditItem?.(item);
    },
    [onEditItem, closeAllCards]
  );

  const handleDelete = useCallback(
    (item: BudgetItem) => {
      closeAllCards();
      onDeleteItem?.(item);
    },
    [onDeleteItem, closeAllCards]
  );

  const handlePayments = useCallback(
    (item: BudgetItem) => {
      closeAllCards();
      onManagePayments?.(item);
    },
    [onManagePayments, closeAllCards]
  );

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          No budget items match your search.
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title="No budget items yet"
          description="Add your expected expenses — a flat amount, a per-guest cost, or price × quantity — then track payments against each."
          iconName="Coin"
          actionLabel="Add budget item"
          onAction={onAddItem}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (item: BudgetItem): boolean =>
    isSelectable && ((!!onEditItem && recordHasRight(item, "edit")) || (!!onDeleteItem && recordHasRight(item, "remove")));

  const rowProps = (item: BudgetItem): RowProps => ({
    item,
    meta: categoryMeta(item),
    onEdit: onEditItem ? handleEdit : undefined,
    onPayments: onManagePayments ? handlePayments : undefined,
    onDelete: onDeleteItem ? handleDelete : undefined,
    onOpenComments: (i) =>
      comments.open({ eventId: i.eventId ?? "", entityKind: "budget", entityId: i.id ?? "", title: i.name ?? undefined }),
    highlighted: item.id === highlightedId,
    isDeleting: isRemoving(item.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(item),
    selected: selection?.isSelected(item.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = data
    .filter((item) => rowIsSelectable(item))
    .map((item) => item.id)
    .filter((id): id is string => !!id);

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label="Select all"
                    />
                  </Table.Th>
                )}
                <Table.Th>Item</Table.Th>
                <Table.Th style={{ width: 160 }}>Category</Table.Th>
                <Table.Th style={{ width: 130 }}>Due</Table.Th>
                <Table.Th style={{ width: 160 }}>Responsible</Table.Th>
                <Table.Th style={{ width: 240 }}>Amount</Table.Th>
                <Table.Th style={{ width: 120 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => <BudgetTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : data.map((item) => <BudgetTableRow key={item.id} {...rowProps(item)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
        {comments.drawer}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <BudgetCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((item) => (
          <BudgetCard
            key={item.id}
            {...rowProps(item)}
            forceClose={shouldForceClose(item.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(item.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(item.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
      {comments.drawer}
    </Stack>
  );
};

export const BudgetItemsList = React.memo(BudgetItemsListComponent);
